// CaptureBreadcrumb — single Back affordance for the field-capture
// drill-down (Base → Site → Building → Room, per PRD §7.1 / buildspec
// 20-ui §3).
//
// Earlier revisions rendered the full ancestor chain ("BURNHAM > MAIN
// CAMP > HQ"). On phones the per-segment 10-ch cap truncated names
// mid-character ("BURN…") and the multi-segment row consumed vertical
// space mobile field staff need for the building list. The component
// now collapses that chain to a single "← Back" control.
//
// The Back control is always a `<Link to={parentTo}>`. Callers MUST
// supply an explicit, deterministic `parentTo` route — the component
// no longer falls back to `navigate(-1)`. Browser-history fallback
// silently no-ops on direct PWA loads, deep links, and reloads, which
// stranded field staff inside the capture flow with no escape (PR #452
// Codex review P2). Making `parentTo` required forces the call site to
// declare the canonical parent at compile time.
//
// Segment props remain optional and are accepted for backwards-compat
// shape only — they are no longer used to derive the destination. The
// only segment that carries meaning today is the leaf, and the leaf
// label is not surfaced in the collapsed Back control. Callers may
// still pass them so the existing breadcrumb-builder helpers
// (building-breadcrumb, room-breadcrumb, …) keep working without an
// invasive shape change. A future cleanup may drop them entirely once
// every helper has been simplified.
//
// Tap target: the back control is min-h-11 / min-w-11 (44 px) per
// AGENTS.md invariant #10 and buildspec 20-ui §9.
//
// Colour: rendered on the Forest brand topbar. The label and chevron
// land on Lime so the control reads at full brand contrast — same
// token the multi-segment trail used previously.

import type { ReactElement } from 'react'
import { ChevronLeft } from 'lucide-react'
import { Link } from 'react-router'

export interface CaptureBreadcrumbSegment {
  label: string
  // Display-only metadata. Retained on the type so existing builder
  // helpers compile unchanged; the value is no longer consumed by the
  // collapsed Back control. The Back destination is `parentTo`.
  to?: string
}

export interface CaptureBreadcrumbProps {
  // Deterministic in-app route the Back control navigates to. Required
  // — every page that mounts this component must declare its canonical
  // parent so direct/deep-link loads and reloads always have a real
  // destination. See PR #452 Codex review P2.
  parentTo: string
  base?: CaptureBreadcrumbSegment
  site?: CaptureBreadcrumbSegment
  building?: CaptureBreadcrumbSegment
  room?: CaptureBreadcrumbSegment
}

type SegmentKey = 'base' | 'site' | 'building' | 'room'

const ORDER: readonly SegmentKey[] = ['base', 'site', 'building', 'room']

// The back control's height matches the previous budget (min-h-9
// mobile, sm:min-h-11 sm+) so the sticky stack stays ≤ 92 px on a
// 360 px viewport (issue #258). min-w-11 keeps the tap target at the
// 44 px floor regardless of label length.
//
// Visibility tweaks (Bug-list "BACK isn't prominent at mobile sizes"):
//   • text-sm at every breakpoint (was text-xs on mobile) — 14 px is
//     the smallest size that still reads at arm's length on a phone
//     held by a gloved field-staff hand.
//   • font-semibold (was font-medium) — distinguishes the control
//     from neighbouring forest-on-forest body copy.
//   • subtle lime/10 background — gives the control a visible button
//     shape on the forest topbar without competing with the page's
//     primary actions, which use solid lime.
//   • chevron h-5 (was h-4) — pairs with the bumped text size so the
//     glyph doesn't look stranded.
const BACK_CLASSES =
  'inline-flex min-h-9 sm:min-h-11 min-w-11 items-center gap-1.5 rounded-md bg-[color:var(--color-cc-lime)]/10 px-2 py-1 text-sm font-semibold uppercase tracking-wide text-[color:var(--color-cc-lime)] hover:bg-[color:var(--color-cc-lime)]/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]'

export function CaptureBreadcrumb(props: CaptureBreadcrumbProps): ReactElement | null {
  const hasAnySegment = ORDER.some((key) => props[key] !== undefined)
  if (!hasAnySegment) return null

  return (
    <nav aria-label="Breadcrumb" className="flex w-full items-center">
      <Link to={props.parentTo} className={BACK_CLASSES} aria-label="Back">
        <ChevronLeft
          aria-hidden="true"
          className="h-5 w-5 shrink-0 text-[color:var(--color-cc-lime)]"
        />
        <span>Back</span>
      </Link>
    </nav>
  )
}
